import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

interface Notification {
  id: string
  tenant_id: string
  application_id: string
  user_id: string | null
  event_type: string
  title: string
  body: string | null
  data: string | null
  priority: 'default' | 'high' | 'low'
  idempotency_key: string | null
  created_at: string
}

interface NotificationsParams {
  application_id?: string
  page?: number
  limit?: number
}

export function useNotifications(params: NotificationsParams = {}) {
  const queryParams = new URLSearchParams()
  if (params.application_id) queryParams.set('application_id', params.application_id)
  if (params.page) queryParams.set('page', String(params.page))
  if (params.limit) queryParams.set('limit', String(params.limit))

  const queryString = queryParams.toString()
  const path = `/dashboard/notifications${queryString ? `?${queryString}` : ''}`

  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => api.get<Notification[]>(path),
  })
}

export function useNotification(id: string) {
  return useQuery({
    queryKey: ['notifications', id],
    queryFn: () => api.get<Notification>(`/dashboard/notifications/${id}`),
    enabled: !!id,
  })
}

interface SendNotificationData {
  application_id: string
  audience: {
    user_id?: string
    user_ids?: string[]
    segment?: string
    all?: boolean
  }
  event: string
  notification: {
    title: string
    body?: string
    image_url?: string
    sound?: string
    badge?: number
  }
  data?: Record<string, unknown>
  priority?: 'default' | 'high' | 'low'
  idempotency_key?: string
}

export function useSendNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: SendNotificationData) => {
      return api.post<Notification>('/v1/notifications', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}
